import { useEffect, useState, useRef, useContext, useCallback } from "react";
import { io } from "socket.io-client";
import Axios from "axios";
import AppContext from "../context/store";

const SOCKET_URL = "https://socket.vatansoft.net";

const getSiteName = () => {
  if (process.env.APP_NAME === "salon") return "salonrandevu";
  if (process.env.APP_NAME === "en") return "enrandevu";
  return "salonrandevu";
};

const useESignatureSocket = () => {
  const context = useContext(AppContext);
  const [eSignatureConnected, setESignatureConnected] = useState(false);
  const [desktopConnected, setDesktopConnected] = useState(false);
  const [certificateExpired, setCertificateExpired] = useState(false);
  const [certificateExpiredDate, setCertificateExpiredDate] = useState(null);
  const [nationalIdMissing, setNationalIdMissing] = useState(false);
  const [tcMismatch, setTcMismatch] = useState(false);
  const [socketError, setSocketError] = useState(null);
  const [mobileLoading, setMobileLoading] = useState(false);
  const [mobileError, setMobileError] = useState(null);
  const socketRef = useRef(null);
  const userNationalIdRef = useRef(null);

  useEffect(() => {
    if (!context.api_endpoint) return;

    let socket = null;

    const fetchUserNationalId = async () => {
      try {
        const userId = context.state?.user?.id;
        if (!userId) return null;
        const { data } = await Axios.get(
          `${context.api_endpoint}/company/staff/${userId}`
        );
        return data?.data?.detail?.national_id ?? null;
      } catch {
        return null;
      }
    };

    const verifyTcNo = (certTcNo) => {
      const userTc = userNationalIdRef.current;
      if (!userTc) {
        setNationalIdMissing(true);
        setTcMismatch(false);
        setESignatureConnected(false);
        return;
      }
      if (String(certTcNo) === String(userTc)) {
        setNationalIdMissing(false);
        setTcMismatch(false);
        setESignatureConnected(true);
        setCertificateExpired(false);
        setSocketError(null);
      } else {
        setNationalIdMissing(false);
        setTcMismatch(true);
        setESignatureConnected(false);
      }
    };

    const connectSocket = async () => {
      try {
        const [tokenResponse, statusResult, nationalId] = await Promise.all([
          Axios.get(`${context.api_endpoint}/esignature/token`),
          Axios.get(`${context.api_endpoint}/esignature/status`)
            .then((res) => res.data)
            .catch((err) => err.response?.data || null),
          fetchUserNationalId(),
        ]);

        userNationalIdRef.current = nationalId;

        if (!nationalId) {
          setNationalIdMissing(true);
          setESignatureConnected(false);
        } else if (statusResult?.active) {
          setESignatureConnected(true);
          setDesktopConnected(true);
          setNationalIdMissing(false);
          setTcMismatch(false);
        }

        const esignatureToken = tokenResponse.data?.data || tokenResponse.data;
        if (!esignatureToken || typeof esignatureToken !== "string") {
          if (!statusResult?.active) {
            setESignatureConnected(false);
          }
          return;
        }

        socket = io(SOCKET_URL, {
          transports: ["websocket", "polling"],
          reconnection: true,
          reconnectionAttempts: 5,
          reconnectionDelay: 3000,
        });
        socketRef.current = socket;

        socket.on("connect", () => {
          socket.emit("identify", {
            type: "browser",
            site: getSiteName(),
            token: esignatureToken,
          });
        });

        socket.on("ready", () => {
          setSocketError(null);
        });

        socket.on("desktop_connected", () => {
          setDesktopConnected(true);
        });

        socket.on("desktop_disconnected", () => {
          setDesktopConnected(false);
          setESignatureConnected(false);
        });

        socket.on("verification_success", ({ cert }) => {
          verifyTcNo(cert?.tcNo);
        });

        socket.on("verification_failed", ({ error }) => {
          setESignatureConnected(false);
          setSocketError(error);
        });

        socket.on("certificate_expired", ({ validTo }) => {
          setESignatureConnected(false);
          setCertificateExpired(true);
          setCertificateExpiredDate(validTo);
        });

        socket.on("token_removed", () => {
          setESignatureConnected(false);
          setDesktopConnected(false);
        });

        socket.on("error", ({ message }) => {
          setSocketError(message);
        });

        socket.on("disconnect", () => {
          setDesktopConnected(false);
        });
      } catch {
        setESignatureConnected(false);
      }
    };

    connectSocket();

    const checkStatusOnFocus = () => {
      if (document.visibilityState === "visible") {
        Axios.get(`${context.api_endpoint}/esignature/status`)
          .then((res) => {
            if (res.data?.active) {
              if (userNationalIdRef.current) {
                setESignatureConnected(true);
                setDesktopConnected(true);
              }
            } else {
              setESignatureConnected(false);
              setDesktopConnected(false);
            }
          })
          .catch((err) => {
            const data = err.response?.data;
            if (data && data.active === false) {
              setESignatureConnected(false);
              setDesktopConnected(false);
            }
          });
      }
    };

    document.addEventListener("visibilitychange", checkStatusOnFocus);

    return () => {
      document.removeEventListener("visibilitychange", checkStatusOnFocus);
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [context.api_endpoint]);

  const mobileSignIn = useCallback(async () => {
    if (!context.api_endpoint || mobileLoading) return;
    setMobileLoading(true);
    setMobileError(null);
    try {
      const res = await Axios.post(`${context.api_endpoint}/esignature/mobile`, {
        timeout: 95000,
      });
      const data = res.data;
      if (data?.active || data?.data?.active || res.status === 200) {
        setESignatureConnected(true);
        setMobileLoading(false);
      } else {
        setMobileError("Mobil onay başarısız oldu.");
        setMobileLoading(false);
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.data ||
        (typeof err?.response?.data === "string" ? err.response.data : null) ||
        "Mobil onay başarısız oldu.";
      setMobileError(msg);
      setMobileLoading(false);
    }
  }, [context.api_endpoint, mobileLoading]);

  return {
    eSignatureConnected,
    desktopConnected,
    certificateExpired,
    certificateExpiredDate,
    nationalIdMissing,
    tcMismatch,
    socketError,
    mobileSignIn,
    mobileLoading,
    mobileError,
  };
};

export default useESignatureSocket;
